import { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  Stack,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme,
} from '@mui/material';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import FormProvider from 'src/components/hook-form/FormProvider';
import Iconify from 'src/components/iconify';
import { HEADER } from 'src/config-global';
import { IUnifiedFilter, useSearchFilterContext } from 'src/contexts/SearchFilterContext';
import { useStores } from 'src/models';
import { isMobile } from 'src/utils/common';

import { FilterSection } from './FilterSection';
import { TooltipContents } from './FilterTooltip';

export type FilterFormType = IUnifiedFilter;

export interface FilterPageConfig {
  sections: FilterSectionKey[];
  defaultValues: Partial<IUnifiedFilter>;
  title: string;
  info: string[];
  customFields?: string[];
}

export type FilterSectionKey = 'order' | 'sales' | 'keyword' | 'common';
const lostCode = 200307;
const createPageConfigs = (codeStore?: any): Record<string, FilterPageConfig> => {
  const getCodeDefaults = () => {
    if (!codeStore?.commonCodes?.length) return {};
    const NEW_STATUS = codeStore.getCodeByName('NEW_STATUS_CD') || [];
    const ONGOING = codeStore.getCodeByName('ONGOING_CD') || [];
    const BZ_STEP =
      codeStore.getCodeByName('BZ_STEP_CD').filter(
        // "실주" 제외
        (v: { value: number | string; label: string }) => String(v.value) !== String(lostCode),
      ) || [];
    const SIGN = codeStore.getCodeByName('SIGN_CD') || [];
    return {
      NEW_STATUS,
      ONGOING,
      SIGN,
      newStatusAll: NEW_STATUS.map((v: { value: number | string; label: string }) =>
        String(v.value),
      ),
      ongoingAll: ONGOING.map((v: { value: number | string; label: string }) => String(v.value)),
      bzStepAll: BZ_STEP.map((v: { value: number | string; label: string }) => v.value),
      firstSign: SIGN[0]?.value || '',
    };
  };
  const codes = getCodeDefaults();

  return {
    '/fc-chart': {
      title: 'FC Chart Filter',
      info: ['Order/Sales 차트를 기저/신규, 스팟/지속, 영업단계, 수주확률로 필터링'],
      // info: ['Order/Sales 차트를 기저/신규, 스팟/지속, 영업단계, 수주/매출확률로 필터링'],
      sections: ['order', 'sales'],
      defaultValues: {
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,

        salesNewStatusCdList: codes.newStatusAll,
        salesOngoingCdList: codes.ongoingAll,
        noSalesBzStepCd: true,
        salesBzStepCdList: codes.bzStepAll,
        salesPrcnt: '',
        salesSign: codes.firstSign,
      },
    },

    '/project': {
      title: 'Project Filter',
      info: [
        '프로젝트를 기저/신규, 스팟/지속, 영업단계, 수주확률, 키워드로 필터링',
        // '키워드로 검색 가능한 항목은 프로젝트명, 고객사, 계약사 , ESM 코드 , UUID , 사업관리코드 , 사업기안번호 입니다.',
      ],
      sections: ['common', 'keyword'],
      defaultValues: {
        newStatusCdList: codes.newStatusAll,
        ongoingCdList: codes.ongoingAll,
        noBzStepCd: true,
        bzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,
        text: '',
      },
    },

    '/dashboard/margin': {
      title: 'Dashboard Filter',
      info: [
        'Margin/Size 타입에서 필터 활성, Speed/FS 타입에서는 비활성',
        'Margin 타입: 기저/신규, 스팟/지속, 영업단계, 수주확률 → Order 차트 적용',
        'Size 타입: 기저/신규, 스팟/지속, 영업단계, 수주확률 → Order/Sales 차트 적용',
      ],
      sections: ['order'],
      defaultValues: {
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,
      },
    },
    '/dashboard/size': {
      title: 'Dashboard Filter',
      sections: ['order', 'sales'],
      info: [
        'Margin/Size 타입에서 필터 활성, Speed/FS 타입에서는 비활성',
        'Margin 타입: 기저/신규, 스팟/지속, 영업단계, 수주확률 → Order 차트 적용',
        'Size 타입: 기저/신규, 스팟/지속, 영업단계, 수주확률 → Order/Sales 차트 적용',
      ],
      defaultValues: {
        orderNewStatusCdList: codes.newStatusAll,
        orderOngoingCdList: codes.ongoingAll,
        noOrderBzStepCd: true,
        orderBzStepCdList: codes.bzStepAll,
        orderPrcnt: '',
        orderSign: codes.firstSign,

        salesNewStatusCdList: codes.newStatusAll,
        salesOngoingCdList: codes.ongoingAll,
        noSalesBzStepCd: true,
        salesBzStepCdList: codes.bzStepAll,
        salesPrcnt: '',
        salesSign: codes.firstSign,
      },
    },
  };
};

export const SearchFilter = observer(() => {
  const { executeCallback, setFilter, openFilter, setOpenFilter } = useSearchFilterContext();

  const theme = useTheme();
  const rootStore = useStores();
  const { commonCodeStore: codeStore } = rootStore;
  const location = useLocation();
  const pathname = location.pathname === '/' ? '/fc-chart' : location.pathname;

  // 마지막으로 제출(저장)된 필터값을 추적
  const lastAppliedFilterRef = useRef<IUnifiedFilter | undefined>(undefined);

  const commonCodes = codeStore.commonCodes;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pageConfigs = useMemo(() => createPageConfigs(codeStore), [commonCodes.length]);
  // const currentConfig = useMemo(
  //   () => pageConfigs[pathname] || pageConfigs['/fc-chart'],
  //   [pathname, pageConfigs],
  // );
  const currentConfig = useMemo(() => {
    if (pageConfigs[pathname]) {
      return pageConfigs[pathname];
    }
    // pageConfigs에 없으면 undefined 반환 (fallback 제거)
    return undefined;
  }, [pathname, pageConfigs]);

  const isCodesLoaded = !!codeStore?.commonCodes?.length;
  const FILTER_SESSION_KEY = pathname === '/' ? 'filter_/fc-chart' : `filter_${pathname}`;

  // SessionStorage에서 필터값 가져오기
  const getStoredFilter = (key: string): IUnifiedFilter | undefined => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  };

  // 현재 페이지의 초기 필터값 계산 (sessionStorage 우선, 없으면 defaultValues)
  const getInitialFilterForPage = (): IUnifiedFilter => {
    if (!isCodesLoaded) return {};

    const storedFilter = getStoredFilter(FILTER_SESSION_KEY);
    return storedFilter || (currentConfig?.defaultValues as IUnifiedFilter) || {};
  };
  const getCodeDefaults = () => {
    if (!codeStore?.commonCodes?.length) return {};
    const NEW_STATUS = codeStore.getCodeByName('NEW_STATUS_CD') || [];
    const ONGOING = codeStore.getCodeByName('ONGOING_CD') || [];
    const BZ_STEP =
      codeStore.getCodeByName('BZ_STEP_CD').filter(
        // "실주" 제외
        (v: { value: number | string; label: string }) => String(v.value) !== String(lostCode),
      ) || [];
    const SIGN = codeStore.getCodeByName('SIGN_CD') || [];

    return {
      NEW_STATUS,
      ONGOING,
      SIGN,
      newStatusAll: NEW_STATUS.map((v: { value: number | string; label: string }) =>
        String(v.value),
      ),
      ongoingAll: ONGOING.map((v: { value: number | string; label: string }) => String(v.value)),
      bzStepAll: BZ_STEP.map((v: { value: number | string; label: string }) => v.value),
      firstSign: Number(SIGN[0]?.value) || undefined,
    };
  };

  const codes = getCodeDefaults();

  const defaultValues: IUnifiedFilter = {
    // Order 관련
    orderNewStatusCdList: codes.newStatusAll ?? [],
    orderOngoingCdList: codes.ongoingAll ?? [],
    noOrderBzStepCd: true,
    orderBzStepCdList: codes.bzStepAll ?? [],
    orderPrcnt: '',
    orderSign: codes.firstSign,

    // Sales 관련
    salesNewStatusCdList: codes.newStatusAll ?? [],
    salesOngoingCdList: codes.ongoingAll ?? [],
    noSalesBzStepCd: true,
    salesBzStepCdList: codes.bzStepAll ?? [],
    salesPrcnt: '',
    salesSign: codes.firstSign,

    // Project 관련
    newStatusCdList: codes.newStatusAll ?? [],
    ongoingCdList: codes.ongoingAll ?? [],
    noBzStepCd: true,
    bzStepCdList: codes.bzStepAll ?? [],
    text: '',
  };
  const getAllPossibleFields = (): IUnifiedFilter => defaultValues;
  const methods = useForm<IUnifiedFilter>({
    defaultValues: defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, setValue, reset, watch, control } = methods;

  // 페이지 변경 시 완전 초기화
  useEffect(() => {
    if (!isCodesLoaded) return;

    // 1. 먼저 모든 필드를 기본값으로 완전 리셋
    const allFields = getAllPossibleFields();
    reset(allFields, {
      keepDefaultValues: false,
      keepValues: false,
      keepErrors: false,
      keepDirty: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false,
    });

    // 2. 새 페이지의 초기값 설정
    const initialFilter = getInitialFilterForPage();

    // 3. 각 필드를 명시적으로 설정
    setTimeout(() => {
      Object.entries(initialFilter).forEach(([key, value]) => {
        setValue(key as keyof IUnifiedFilter, value, {
          shouldValidate: false,
          shouldDirty: false,
        });
      });

      lastAppliedFilterRef.current = initialFilter;
      const cleanedData = cleanFilterData(initialFilter);
      setFilter(cleanedData);

      // API 호출
      // executeCallback(cleanedData);
      if (!(pathname === '/project' && location.search)) {
        executeCallback(cleanedData);
      }
    }, 0);
  }, [pathname, isCodesLoaded]); // pathname 변경 시마다 실행

  // 코드가 로드되면 초기 필터값 설정
  useEffect(() => {
    if (isCodesLoaded) {
      const initialFilter = getInitialFilterForPage();
      reset(initialFilter, { keepDefaultValues: false });
      lastAppliedFilterRef.current = initialFilter;

      const cleanedData = cleanFilterData(initialFilter);
      setFilter(cleanedData);
      // executeCallback(cleanedData);
    }
  }, [isCodesLoaded, pathname]); // pathname이 변경될 때만 실행

  // 필터 열기
  const onOpenFilter = () => {
    setOpenFilter(true);
    sessionStorage.setItem('lastFilterOpen', 'true');
    // 필터가 열릴 때 마지막으로 적용된 값으로 복원
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }
  };

  // 필터 닫기 (조회하지 않고 닫는 경우)
  const onCloseFilter = () => {
    setOpenFilter(false);
    sessionStorage.setItem('lastFilterOpen', 'false');
    // 마지막으로 저장된 값으로 복원
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }
  };

  // 조회 버튼
  const onSubmit: SubmitHandler<IUnifiedFilter> = async (data) => {
    const defaultFilter = currentConfig?.defaultValues || defaultValues;

    // default와 동일한 경우 sessionStorage 제거
    if (_.isEqual(data, defaultFilter)) {
      sessionStorage.removeItem(FILTER_SESSION_KEY);
      lastAppliedFilterRef.current = defaultFilter;
    } else {
      // 2️⃣ 변경된 값만 sessionStorage 저장
      sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify(data));
      lastAppliedFilterRef.current = data;
    }

    const cleanedData = cleanFilterData(data);

    // 조회 실행 후 필터 닫기
    await executeCallback(cleanedData);
    // setOpenFilter(false);
  };

  // 필터 초기화
  const handleReset = () => {
    if (!isCodesLoaded) return;
    const defaultFilter = currentConfig?.defaultValues ?? defaultValues;

    reset(defaultFilter);
  };

  const isFilterAvailable = useMemo(() => {
    const explicitFilterPages = ['/fc-chart', '/project', '/dashboard/margin', '/dashboard/size'];

    // 명시적 목록에 있으면 우선 처리
    if (explicitFilterPages.includes(pathname)) {
      // project 페이지는 URL parameter 체크
      if (pathname === '/project') {
        const params = Object.fromEntries(
          Array.from(new URLSearchParams(location.search).entries()).filter(
            ([, value]) => value && value !== 'null' && value !== 'undefined',
          ),
        );
        const hasUrlParams = Object.keys(params).length > 0;
        return !hasUrlParams; // URL parameter가 없을 때만 필터 사용 가능
      }
      return true;
    }

    // 기존 로직 (pageConfigs 기반)
    const hasPageConfig = Object.keys(pageConfigs).includes(pathname);
    return hasPageConfig && isCodesLoaded;
  }, [pathname, location.search, isCodesLoaded]);

  // 페이지 변경 시 필터 reset
  useEffect(() => {
    if (lastAppliedFilterRef.current) {
      reset(lastAppliedFilterRef.current);
    }
    // if (!isFilterAvailable && openFilter) onCloseFilter();
    const timer = setTimeout(() => {
      if (isFilterAvailable) {
        const last = sessionStorage.getItem('lastFilterOpen');
        if (last === 'true') setOpenFilter(true);
      } else {
        setOpenFilter(false);
        if (lastAppliedFilterRef.current) {
          reset(lastAppliedFilterRef.current);
        }
      }
    }, 50); // 50ms 지연 : dashboard filter 닫힘현상
    return () => clearTimeout(timer);
  }, [pathname, isFilterAvailable]);

  const FilterInfo = () => {
    return (
      <Stack p={1}>
        <Typography variant="subtitle1">{currentConfig?.title} 사용 안내</Typography>
        <List sx={{ '& .MuiListItem-root': { px: 0 } }}>
          {currentConfig?.info?.map((v, i) => (
            <ListItem key={'list-item' + i}>
              <Typography variant="body1">• {v}</Typography>
            </ListItem>
          ))}
          <ListItem>
            <Typography variant="body1">• 신규/기저, 스팟/지속: 한 개 이상 선택 필요</Typography>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              • 영업단계: '선택안함' 기본, 해제 시 다중 선택 가능
            </Typography>
          </ListItem>
          <ListItem>
            <Typography variant="body1">
              • 수주확률: '선택안함' 기본, 선택 시 Higher(이상)/Equal(같음)/Lower(이하) 선택 가능
            </Typography>
          </ListItem>
        </List>
      </Stack>
    );
  };
  const drawerRoot = document.getElementById('drawer-root');
  return (
    <>
      <IconButton
        onClick={openFilter ? onCloseFilter : onOpenFilter}
        disabled={!isFilterAvailable}
        sx={{ width: 42 }}
      >
        <LightTooltip
          title={
            currentConfig &&
            !openFilter && <TooltipContents currentValues={watch()} layout={currentConfig} />
          }
          arrow
        >
          {isFilterAvailable ? (
            <Iconify icon={'mage:filter-fill'} color={theme.palette.primary.main} />
          ) : (
            <Iconify icon={'mage:filter'} />
          )}
        </LightTooltip>
      </IconButton>
      {/* Drawer를 Portal로 */}
      {drawerRoot &&
        ReactDOM.createPortal(
          <Drawer
            open={openFilter}
            onClose={onCloseFilter}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: isMobile() ? 375 : 400,
                flexShrink: 0,
                p: 2,
                pt: `${HEADER.H_MAIN_DESKTOP + 16}px`,
                zIndex: theme.zIndex.appBar - 1,
                [`& .MuiDrawer-paper`]: { boxSizing: 'border-box', overflow: 'auto' },
              },
            }}
          >
            {openFilter && currentConfig && (
              <>
                <Stack
                  position={'absolute'}
                  right="0"
                  direction="row"
                  justifyContent="flex-end"
                  pr={2}
                >
                  <Tooltip arrow title={FilterInfo()}>
                    <Iconify
                      icon={'material-symbols:info-outline-rounded'}
                      m={1}
                      color={theme.palette.info.main}
                    />
                  </Tooltip>
                  <IconButton onClick={onCloseFilter}>
                    <Iconify icon="mdi:close" />
                  </IconButton>
                </Stack>
                <FormProvider
                  methods={methods}
                  onSubmit={handleSubmit(onSubmit)}
                  formId="chart-filter"
                >
                  <Stack gap={4}>
                    {isCodesLoaded && currentConfig ? (
                      currentConfig.sections.map((section) => {
                        return (
                          <FilterSection
                            key={section}
                            section={section}
                            color={
                              section === 'order' || section === 'common'
                                ? theme.palette.primary.light
                                : theme.palette.secondary.main
                            }
                            control={control}
                            watch={watch}
                            codeStore={codeStore}
                            pathname={pathname}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" align="center" sx={{ py: 4 }}>
                        필터 옵션을 불러오는 중...
                      </Typography>
                    )}

                    <Stack direction="row" justifyContent="flex-end" gap={1}>
                      <IconButton onClick={handleReset}>
                        <Iconify icon={'material-symbols:refresh'} />
                      </IconButton>

                      <Button type="submit" variant="contained">
                        저장 및 조회
                      </Button>
                    </Stack>
                  </Stack>
                </FormProvider>
              </>
            )}
          </Drawer>,
          document.getElementById('drawer-root') as HTMLElement,
        )}
    </>
  );
});

export default SearchFilter;

// filter data 정제
function cleanFilterData(data: IUnifiedFilter): IUnifiedFilter {
  const formatted = _.mapValues(data, (value) =>
    Array.isArray(value) ? value.join(',') : value || '',
  );

  // 불필요한 필드 제거
  const filtered = _.omit(formatted, ['noBzStepCd', 'noOrderBzStepCd', 'noSalesBzStepCd']);
  // 빈 값 제거
  const pureFilter = _.pickBy(
    filtered,
    (value) => value !== '' && value !== undefined && value !== null,
  );
  if (formatted.noBzStepCd) delete pureFilter.bzStepCdList;
  if (formatted.noOrderBzStepCd) delete pureFilter.orderBzStepCdList;
  if (formatted.noSalesBzStepCd) delete pureFilter.salesBzStepCdList;
  if (!formatted.orderPrcnt) {
    delete pureFilter.orderPrcnt;
    delete pureFilter.orderSign;
  }
  if (!formatted.salesPrcnt) {
    delete pureFilter.salesPrcnt;
    delete pureFilter.salesSign;
  }

  return pureFilter;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.neutral,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.neutral,
  },
}));
